<<template>
  <div class="biz-shell">
    <!-- Ambient Background -->
    <div class="ambient-layer">
      <div class="orb orb-blue" />
      <div class="orb orb-teal" />
      <div class="orb orb-gold" />
    </div>

    <!-- Toolbar -->
    <v-toolbar dense tabs color="transparent" dark class="glass-toolbar" elevation="0">
      <v-toolbar-title class="pa-0 pr-0 mt-1" style="width: 100%;">
        <v-layout row align-center>
          <v-flex xs10 sm11 text-xs-left class="toolbar-brand">
            <a href="javascript: history.go(-1)" class="pa-0 back-link">
              <v-btn flat fab small class="pa-0 btn-glass">
                <i class="fas fa-arrow-left headline"></i>
              </v-btn>
            </a>
            <span class="toolbar-title-text">Business Suite</span>
          </v-flex>
          <v-flex xs2 sm1 text-xs-right>
            <router-link :to="$store.state.close_url3" class="pa-0 text-xs-right">
              <v-btn flat fab small class="pa-0 btn-glass">
                <i class="fas fa-close headline"></i>
              </v-btn>
            </router-link>
          </v-flex>
        </v-layout>
      </v-toolbar-title>
    </v-toolbar>

    <!-- Main Content -->
    <v-container class="main-container">
      <v-layout row>
        <v-flex xs12 md8 lg6 offset-md2 offset-lg3>
          <v-slide-y-transition mode="out-in">
            <div class="biz-content">

              <!-- Hero -->
              <div class="welcome-block animate-enter">
                <div class="welcome-eyebrow">Upgrade to</div>
                <h1 class="welcome-title">
                  <span class="petty-blue">Petty</span><span class="cash-green">Cash</span>
                  <span class="sas-gold"> SAS</span>
                </h1>
                <p class="welcome-text">
                  Unlock Business Suite for <span class="price-highlight">10,500 FCFA</span>. Scale your business with advanced tools and earn up to <span class="commission-highlight">55% commission</span>.
                </p>
              </div>

              <!-- Features Grid -->
              <div class="features-strip animate-enter" style="animation-delay: .1s">
                <div class="feature-chip">
                  <v-icon small color="#00479B">mdi-briefcase</v-icon>
                  <span>Business Tools</span>
                </div>
                <div class="feature-chip">
                  <v-icon small color="#10B981">mdi-account-group</v-icon>
                  <span>Multi-user</span>
                </div>
                <div class="feature-chip">
                  <v-icon small color="#F97316">mdi-cash-multiple</v-icon>
                  <span>55% Commission</span>
                </div>
              </div>

              <!-- Form Card -->
              <v-card class="glass-form-card pa-4 animate-enter" style="animation-delay: .2s" elevation="0">
                <v-form ref="signupForm" v-model="formValid" lazy-validation @submit.prevent="submitForm">

                  <!-- Full Name -->
                  <div class="field-group">
                    <div class="field-icon-wrap">
                      <div class="field-icon-circle teal-glow">
                        <i class="fas fa-user"></i>
                      </div>
                    </div>
                    <div class="field-input-wrap">
                      <v-text-field
                        class="modern-input"
                        v-model="form.name"
                        label="Full name"
                        :rules="nameRules"
                        solo
                        flat
                      ></v-text-field>
                    </div>
                    <v-btn @click.stop="triggerHelp('fullnames')" flat fab small class="help-btn">
                      <i class="fas fa-question"></i>
                    </v-btn>
                  </div>

                  <!-- Username -->
                  <div class="field-group">
                    <div class="field-icon-wrap">
                      <div class="field-icon-circle teal-glow">
                        <i class="fas fa-user-circle"></i>
                      </div>
                    </div>
                    <div class="field-input-wrap">
                      <v-text-field
                        class="modern-input"
                        v-model="form.username"
                        label="Username"
                        :rules="usernameRules"
                        solo
                        flat
                      ></v-text-field>
                    </div>
                    <v-btn @click.stop="triggerHelp('username')" flat fab small class="help-btn">
                      <i class="fas fa-question"></i>
                    </v-btn>
                  </div>

                  <!-- Email -->
                  <div class="field-group">
                    <div class="field-icon-wrap">
                      <div class="field-icon-circle blue-glow">
                        <i class="fas fa-envelope"></i>
                      </div>
                    </div>
                    <div class="field-input-wrap">
                      <v-text-field
                        class="modern-input"
                        v-model="form.email"
                        label="Email address"
                        :rules="emailRules"
                        solo
                        flat
                      ></v-text-field>
                    </div>
                    <v-btn @click.stop="triggerHelp('email')" flat fab small class="help-btn">
                      <i class="fas fa-question"></i>
                    </v-btn>
                  </div>

                  <!-- Phone -->
                  <div class="field-group">
                    <div class="field-icon-wrap">
                      <div class="field-icon-circle orange-glow">
                        <i class="fas fa-phone"></i>
                      </div>
                    </div>
                    <div class="field-input-wrap phone-wrap">
                      <input
                        @click="clearPhoneField()"
                        @blur="validatePhone"
                        id="phone"
                        type="tel"
                        v-model="phone_number"
                        ref="phone_number"
                        name="phone"
                        class="phone-input"
                        :class="{ 'has-error': phoneErrorMessage }"
                      />
                      <div v-if="phoneErrorMessage" class="field-error">
                        {{ phoneErrorMessage }}
                      </div>
                    </div>
                    <v-btn @click.stop="triggerHelp('phone')" flat fab small class="help-btn">
                      <i class="fas fa-question"></i>
                    </v-btn>
                  </div>

                  <!-- Password -->
                  <div class="field-group">
                    <div class="field-icon-wrap">
                      <div class="field-icon-circle purple-glow">
                        <i class="fas fa-lock"></i>
                      </div>
                    </div>
                    <div class="field-input-wrap">
                      <v-text-field
                        class="modern-input"
                        :append-icon="e1 ? 'visibility_off' : 'visibility'"
                        @click:append="() => (e1 = !e1)"
                        :type="e1 ? 'text' : 'password'"
                        v-model="form.password"
                        label="Password"
                        :rules="passwordRules"
                        solo
                        flat
                      ></v-text-field>
                    </div>
                    <v-btn @click.stop="triggerHelp('password')" flat fab small class="help-btn">
                      <i class="fas fa-question"></i>
                    </v-btn>
                  </div>

                  <!-- Referral -->
                  <div class="field-group referral-group">
                    <div class="field-icon-wrap">
                      <div class="field-icon-circle gold-glow">
                        <i class="fas fa-code"></i>
                      </div>
                    </div>
                    <div class="field-input-wrap">
                      <ReferralField
                        v-model="referralCode"
                        :state="referralState"
                        :ownerName="referralOwnerName"
                        :locked="referralLocked"
                        @input="onReferralInput"
                      />
                    </div>
                    <v-btn @click.stop="triggerHelp('referral')" flat fab small class="help-btn">
                      <i class="fas fa-question"></i>
                    </v-btn>
                  </div>

                  <!-- Submit -->
                  <div class="submit-wrap">
                    <v-btn
                      block
                      @click="submitForm"
                      class="btn-glass-primary btn-large"
                      type="submit"
                      :loading="$store.state.isLoading"
                    >
                      <span>Unlock for 10,500 FCFA</span>
                      <v-icon right small>mdi-arrow-right</v-icon>
                    </v-btn>
                  </div>

                </v-form>

                <!-- Login Link -->
                <div class="login-link-wrap">
                  <p class="login-text">
                    Already have a Basic account?
                    <router-link :to="{
                      name: 'create_business_account3',
                      query: { ref: $route.query.ref || $route.query.referral || referralCode || '' }
                    }" class="login-link">
                      Upgrade here
                    </router-link>
                  </p>
                  <p class="login-text mt-2">
                    Or <a href="/login" class="login-link">Log In</a>
                  </p>
                </div>
              </v-card>

              <!-- Trust -->
              <div class="biz-trust-footer animate-enter" style="animation-delay: .3s">
                <div class="trust-pill">
                  <v-icon small color="#00479B">mdi-shield-check</v-icon>
                  <span>Secure Payment</span>
                </div>
                <div class="trust-pill">
                  <v-icon small color="#10B981">mdi-certificate</v-icon>
                  <span>Licensed FinTech</span>
                </div>
              </div>

            </div>
          </v-slide-y-transition>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- ═══════════════════════════════════════
         PAYMENT DIALOG (Premium Glass)
         ═══════════════════════════════════════ -->
    <v-dialog width="520" v-model="showPayment" content-class="payment-dialog-glass">
      <v-card class="payment-glass-card">
        <div class="payment-header">
          <div class="payment-header-content">
            <v-icon color="white" size="32">mdi-credit-card-check</v-icon>
            <div>
              <div class="payment-title">Confirm Deposit</div>
              <div class="payment-subtitle">10,500 FCFA</div>
            </div>
          </div>
        </div>

        <v-card-text class="pa-4">
          <!-- Transaction Phone -->
          <div class="field-group payment-field">
            <div class="field-icon-wrap">
              <div class="field-icon-circle orange-glow">
                <i class="fas fa-phone"></i>
              </div>
            </div>
            <div class="field-input-wrap phone-wrap">
              <input
                id="transactionPhone"
                type="tel"
                @click="clearTransactionPhoneField()"
                @blur="validateTransactionPhone"
                class="phone-input"
                :class="{ 'has-error': transactionPhoneErrorMessage }"
              />
              <div v-if="transactionPhoneErrorMessage" class="field-error">
                {{ transactionPhoneErrorMessage }}
              </div>
            </div>
            <v-btn @click.stop="triggerHelp('phone')" flat fab small class="help-btn">
              <i class="fas fa-question"></i>
            </v-btn>
          </div>

          <!-- Operators -->
          <div class="operators-grid">
            <v-card
              @click="selectOperator('MTN')"
              :class="{ 'is-selected': paymentMethod === 'MTN' }"
              class="operator-card-glass"
            >
              <v-img :src="require('@/assets/mtn-mobile-money-logo.png')" contain height="50"></v-img>
              <div class="operator-label">MTN Mobile Money</div>
              <div v-if="paymentMethod === 'MTN'" class="operator-check">
                <v-icon small color="white">check</v-icon>
              </div>
            </v-card>

            <v-card
              @click="selectOperator('OM')"
              :class="{ 'is-selected': paymentMethod === 'OM' }"
              class="operator-card-glass"
            >
              <v-img :src="require('@/assets/om.png')" contain height="50"></v-img>
              <div class="operator-label">Orange Money</div>
              <div v-if="paymentMethod === 'OM'" class="operator-check">
                <v-icon small color="white">check</v-icon>
              </div>
            </v-card>
          </div>

          <!-- Summary -->
          <div class="payment-summary">
            <p class="summary-text">
              By clicking <b class="primary--text">Confirm</b>, a deposit of
              <b>10,500 F CFA</b> will be triggered via
              <b v-if="paymentMethod">{{ paymentMethod === 'MTN' ? 'MTN Mobile Money' : 'Orange Money' }}</b>
              <b v-else class="red--text">[select method]</b>
              from <b>{{ transactionPhoneNumber || '[phone number]' }}</b>
            </p>
          </div>

          <!-- USSD Instructions -->
          <transition name="expand">
            <div v-if="paymentMethod" class="ussd-section">
              <p class="ussd-intro">If you are facing difficulties, send manually through the code below:</p>
              <div class="ussd-code-wrap">
                <code class="ussd-code">{{ ussdCode }}</code>
                <v-btn icon small @click="copyUSSD" class="copy-btn" title="Copy USSD code">
                  <v-icon small>mdi-content-copy</v-icon>
                </v-btn>
              </div>
              <ul class="ussd-steps">
                <li v-if="paymentMethod === 'MTN'">Choose reference 1</li>
                <li v-if="paymentMethod === 'OM'">Input transaction amount</li>
                <li>Always <b class="primary--text">Confirm</b> below</li>
              </ul>
            </div>
          </transition>
        </v-card-text>

        <v-card-actions class="payment-actions">
          <v-btn color="#64748B" text @click="showPayment = false" class="btn-cancel">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            class="btn-confirm white--text"
            @click="handlePaymentConfirmation"
            :disabled="!paymentMethod || !transactionPhoneNumber"
            :loading="processingPayment"
          >
            Confirm Payment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import referralMixin from '@/mixins/referralMixin';
import ReferralField from '@/components/ReferralField.vue';

export default {
  mixins: [referralMixin],
  components: { ReferralField },

  data() {
    return {
      formValid: false,
      showCreateAccountOptions: false,
      e1: false,
      transactionPhoneInput: null,
      fullnames_help: false,
      username_help: false,
      email_help: false,
      telephone_help: false,
      password_help: false,
      reference_help: false,
      phone_number_help: false,
      form: {
        name: '',
        username: '',
        telephone: '',
        password: '',
        email: '',
      },
      showPayment: false,
      paymentMethod: '',
      transactionPhoneNumber: '',
      transactionId: '',
      ussdCode: '',
      ussdCode2: '',
      hrefTel: '',
      phone_number: "Enter Phone Number",
      alert_message: "",
      phoneInput: null,
      transfer_code: '',
      processingPayment: false,
      phoneErrorMessage: '',
      transactionPhoneErrorMessage: '',

      nameRules: [
        v => !!v || 'Name is required',
        v => (v && v.length >= 2) || 'Name must be at least 2 characters',
      ],
      usernameRules: [
        v => !!v || 'Username is required',
        v => (v && v.length >= 4) || 'Username must be at least 4 characters',
      ],
      passwordRules: [
        v => !!v || 'Password is required',
        v => (v && v.length >= 8) || 'Password must be at least 8 characters',
      ],
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid',
      ],
      referralRules: [
        v => !!v || 'Referral code is required',
        v => (v && v.length >= 5) || 'Referral code must be at least 5 characters',
      ],
    };
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    isReferralLocked() {
      return this.referralLocked && this.referralCode && this.referralCode !== '020PC1';
    }
  },

  mounted() {
    const referralFromURL = this.$route.query.referral || this.$route.query.affiliate_referral;
    this.form.affiliate_referral = referralFromURL || '020PC1';

    console.log('🔑 Referral code chargé:', this.form.affiliate_referral);
    console.log('🔒 Champ verrouillé?', this.isReferralLocked);

    if (this.$store.state.isAuthenticated) {
      this.$store.commit('setSnackBarMessage', "You are currently signed into an account.");
      this.$store.commit('setSnackBarColor', "red darken-4");
      this.$store.commit('activateSnackBar', true);
      this.$router.push("/All");
    }

    this.initialiseIntel();
    this.$store.commit('setSaveItem', "");
  },

  watch: {
    showPayment(newVal) {
      if (newVal) {
        this.$nextTick(() => this.initialiseTransactionIntel());
      }
    },
  },

  methods: {
    triggerHelp(field) {
      this.fullnames_help = false;
      this.username_help = false;
      this.email_help = false;
      this.password_help = false;
      this.reference_help = false;
      this.phone_number_help = false;

      if (field === 'fullnames') this.fullnames_help = true;
      if (field === 'username') this.username_help = true;
      if (field === 'email') this.email_help = true;
      if (field === 'password') this.password_help = true;
      if (field === 'referral') this.reference_help = true;
      if (field === 'phone') this.phone_number_help = true;

      this.getSignUpFieldHelp();
    },

    getIp(callback) {
      fetch('https://ipinfo.io/json?token=fa5e74d3e65812', {
        headers: { 'Accept': 'application/json' }
      })
      .then((resp) => resp.json())
      .catch(() => {
        return { country: 'cm' };
      })
      .then((resp) => callback(resp.country));
    },

    initialiseIntel() {
      const phoneInputField = document.querySelector("#phone");
      this.phoneInput = window.intlTelInput(phoneInputField, {
        initialCountry: "cm",
        geoIpLookup: this.getIp,
        preferredCountries: ["cm", "us", "uk", "ci", "cg", "cf", "td", "ng", "gq", "ga"],
        utilsScript: "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
      });
    },

    clearPhoneField() {
      if (this.phone_number === "Enter Phone Number") {
        this.phone_number = "";
      }
      this.phoneErrorMessage = '';
    },

    validatePhone() {
      if (!this.phone_number || this.phone_number === "Enter Phone Number") {
        this.phoneErrorMessage = 'Phone number is required';
        return false;
      }
      if (!this.phoneInput || !this.phoneInput.isValidNumber()) {
        this.phoneErrorMessage = 'Phone number is invalid. Please input a valid number';
        return false;
      }
      this.phoneErrorMessage = '';
      return true;
    },

    initialiseTransactionIntel() {
      const transactionPhoneInputField = document.querySelector("#transactionPhone");
      this.transactionPhoneInput = window.intlTelInput(transactionPhoneInputField, {
        initialCountry: "cm",
        geoIpLookup: this.getIp,
        preferredCountries: ["cm", "us", "uk", "ci", "cg", "cf", "td", "ng", "gq", "ga"],
        utilsScript: "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
      });

      transactionPhoneInputField.addEventListener('input', () => {
        this.transactionPhoneNumber = this.transactionPhoneInput.getNumber();
        this.transactionPhoneErrorMessage = '';
      });
    },

    clearTransactionPhoneField() {
      const currentValue = this.transactionPhoneInput?.getNumber();
      if (!currentValue || currentValue === "Enter Phone Number") {
        this.transactionPhoneInput?.setNumber("");
      }
      this.transactionPhoneErrorMessage = '';
    },

    validateTransactionPhone() {
      if (!this.transactionPhoneInput || !this.transactionPhoneInput.isValidNumber()) {
        this.transactionPhoneErrorMessage = 'Phone number is invalid. Please input a valid number';
        return false;
      }
      this.transactionPhoneErrorMessage = '';
      return true;
    },

    getSignUpFieldHelp() {
      if (this.fullnames_help) {
        this.fullnames_help = false;
        this.$store.commit('setDialogTitle', "Full Names");
        this.$store.commit('setDialogMessage', "Here you are required to input all your names.");
        this.$store.commit('activateDialog', true);
      } else if (this.username_help) {
        this.username_help = false;
        this.$store.commit('setDialogTitle', "Your User Name");
        this.$store.commit('setDialogMessage', "Here you are required to input your User Name.");
        this.$store.commit('activateDialog', true);
      } else if (this.password_help) {
        this.password_help = false;
        this.$store.commit('setDialogTitle', "Your Password");
        this.$store.commit('setDialogMessage', "You are required to input your account password. This password needs to be at least 8 characters long. It is also advisable for you to write down this password so in case u forget, you can know where to get it.");
        this.$store.commit('activateDialog', true);
      } else if (this.email_help) {
        this.email_help = false;
        this.$store.commit('setDialogTitle', "why have an email");
        this.$store.commit('setDialogMessage', "Your email will allow us to send you information and notifications regarding your business suite.");
        this.$store.commit('activateDialog', true);
      } else if (this.phone_number_help) {
        this.phone_number_help = false;
        this.$store.commit('setDialogTitle', "Your Phone Number");
        this.$store.commit('setDialogMessage', "Please input your phone number. This number will be used in sending of money and validating transactions. so make sure it is the right number.");
        this.$store.commit('activateDialog', true);
      } else if (this.reference_help) {
        this.reference_help = false;
        this.$store.commit('setDialogTitle', "Who Referred You");
        this.$store.commit('setDialogMessage', "This referral code links you to your sponsor. If you were referred by someone, their code will be automatically filled and locked.");
        this.$store.commit('activateDialog', true);
      }
    },

    async submitForm() {
      this.phoneErrorMessage = '';
      if (!this.$refs.signupForm.validate()) {
        this.showSnackbar('Please fill all required fields correctly', 'error');
        return;
      }
      if (!this.validatePhone()) {
        this.showSnackbar(this.phoneErrorMessage, 'error');
        return;
      }

      try {
        this.showSnackbar('Validating credentials...', 'info');
        const response = await axios.post('/api/v1/home/validate-credentials/', {
          username: this.form.username,
          phone: this.phoneInput.getNumber(),
          password: this.form.password
        });

        console.log('✅ Validation response:', response.data);

        if (response.data?.error) {
          this.showSnackbar(response.data.error, 'error');
          return;
        }
        if (response.data?.success === false) {
          this.showSnackbar(response.data.message || 'Validation failed', 'error');
          return;
        }

        this.showPayment = true;
        this.showSnackbar('Credentials validated! Please enter your transaction number and select the payment method.', 'success');
        this.paymentMethod = '';
        this.ussdCode = '';
        this.transactionId = '';
        this.transactionPhoneNumber = '';

      } catch (error) {
        this.handleApiError(error, 'Validation failed');
      }
    },

    selectOperator(operator) {
      this.paymentMethod = operator;
      this.generateUSSD();
      this.transactionId = this.generateTransactionId();
      this.showSnackbar(`Selected: ${operator === 'MTN' ? 'MTN Mobile Money' : 'Orange Money'}`, 'info');
    },

    generateTransactionId() {
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 1000);
      return `${this.paymentMethod}-${timestamp}-${random}`;
    },

    generateUSSD() {
      const amount = 10500;
      if (this.paymentMethod === "MTN") {
        this.ussdCode = `*126*4*${this.$store.state.MerchantCodeMTN}*${amount}#`;
        this.transfer_code = `*126*4*${this.$store.state.MerchantCodeMTN}*${amount}%23`;
        this.hrefTel = `tel:*126*4*${this.$store.state.MerchantCodeMTN}*${amount}%23`;
      } else if (this.paymentMethod === "OM") {
        this.transfer_code = `%23150*47*${this.$store.state.MerchantCodeOrange}*${amount}%23`;
        this.ussdCode = `#150*47*${this.$store.state.MerchantCodeOrange}*${amount}#`;
        this.hrefTel = `tel:%23150*47*${this.$store.state.MerchantCodeOrange}*${amount}%23`;
      }
    },

    async handlePaymentConfirmation() {
      if (!this.paymentMethod) {
        this.showSnackbar('Please select payment operator', 'error');
        return;
      }
      if (!this.validateTransactionPhone()) {
        this.showSnackbar(this.transactionPhoneErrorMessage, 'error');
        return;
      }

      try {
        this.generateUSSD();
        window.location.href = this.hrefTel;
        await this.confirmPayment();
        setTimeout(() => {
          if (!document.hidden) {
            window.open(this.hrefTel, '_blank');
          }
        }, 200);
      } catch (error) {
        this.showSnackbar(error.message, 'error');
      }
    },

    async confirmPayment() {
      this.processingPayment = true;
      try {
        this.showSnackbar('Creating your account...', 'info');
        const response = await axios.post('/api/v1/savings/business-account/', this.createPayload());
        console.log('✅ Payment response:', response.data);

        if (response.data.success === false || response.data.error) {
          this.showSnackbar(response.data.error || response.data.message || "Account creation failed.", 'error');
          return;
        }

        await this.handleSuccessFlow(response);

      } catch (error) {
        this.handleApiError(error, 'Account creation failed');
      } finally {
        this.processingPayment = false;
      }
    },

    createPayload() {
      const payload = {
        fullnames: this.form.name,
        username: this.form.username,
        password: this.form.password,
        phone_number: this.phoneInput.getNumber(),
        payment_method: this.paymentMethod,
        email: this.form.email,
        transaction_id: this.transactionId,
        transaction_phone: this.transactionPhoneInput.getNumber(),
        serial_no: this.getReferralPayload()
      };
      console.log('[create_business_account5] Payload:', { payload, referralCode: this.referralCode, getReferralPayload: this.getReferralPayload() });
      return payload;
    },

    async handleSuccessFlow(_response) {
      this.showSuccessMessage();
      await this.handleAutoLogin();
      this.redirectToCampaign();
    },

    showSuccessMessage() {
      this.$store.commit('setSnackBarMessage', "🎉 Account created! Confirmation email after verification. Redirecting...");
      this.$store.commit('setSnackBarColor', "teal darken-2");
      this.$store.commit('activateSnackBar', true);
    },

    async handleAutoLogin() {
      try {
        const { data } = await axios.post('/api/v1/token/login/', {
          username: this.form.username,
          password: this.form.password
        });
        this.$store.commit('setToken', data.auth_token);
        axios.defaults.headers.common['Authorization'] = "Token " + data.auth_token;
        localStorage.setItem('token', data.auth_token);
      } catch (error) {
        this.handleLoginError(error);
        throw new Error('Auto-login failed');
      }
    },

    redirectToCampaign() {
      setTimeout(() => {
        this.$router.push({
          path: '/savings/campaigns/create/new_campaign',
          query: { username: this.form.username, password: this.form.password }
        });
        this.resetForm();
        this.showPayment = false;
      }, 6000);
    },

    handleLoginError(error) {
      console.error('Login error:', error);
      if (error.response) {
        const errorData = error.response.data;
        let errorMessage = '';
        if (errorData.error) errorMessage = errorData.error;
        else if (errorData.non_field_errors) errorMessage = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors.join(', ') : errorData.non_field_errors;
        else if (errorData.detail) errorMessage = errorData.detail;
        else if (typeof errorData === 'object') {
          const msgs = [];
          for (const field in errorData) {
            if (Array.isArray(errorData[field])) msgs.push(`${field}: ${errorData[field][0]}`);
            else if (typeof errorData[field] === 'string') msgs.push(`${field}: ${errorData[field]}`);
          }
          errorMessage = msgs.join('\n') || "Login error";
        } else errorMessage = "Login error";
        this.$store.commit('setSnackBarMessage', errorMessage);
        this.$store.commit('setSnackBarColor', 'red darken-4');
        this.$store.commit('activateSnackBar', true);
      } else {
        this.$store.commit('setSnackBarMessage', "Network error during login");
        this.$store.commit('setSnackBarColor', 'red darken-4');
        this.$store.commit('activateSnackBar', true);
      }
    },

    handleApiError(error, fallbackMsg) {
      console.error('❌ Error:', error);
      let errorMessage = fallbackMsg;
      if (error.response && error.response.data) {
        const d = error.response.data;
        if (d.error) errorMessage = d.error;
        else if (d.message) errorMessage = d.message;
        else if (d.detail) errorMessage = d.detail;
        else if (d.non_field_errors) errorMessage = Array.isArray(d.non_field_errors) ? d.non_field_errors.join(', ') : d.non_field_errors;
        else if (d.errors && typeof d.errors === 'object') {
          const msgs = [];
          for (const f in d.errors) {
            if (Array.isArray(d.errors[f])) msgs.push(`${f}: ${d.errors[f].join(', ')}`);
            else msgs.push(`${f}: ${d.errors[f]}`);
          }
          errorMessage = msgs.join('\n');
        } else if (typeof d === 'object') {
          const msgs = [];
          for (const f in d) {
            if (f !== 'success' && f !== 'status') {
              if (Array.isArray(d[f])) msgs.push(`${f}: ${d[f].join(', ')}`);
              else if (typeof d[f] === 'string') msgs.push(`${f}: ${d[f]}`);
            }
          }
          if (msgs.length > 0) errorMessage = msgs.join('\n');
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else {
        errorMessage = error.message || fallbackMsg;
      }
      this.showSnackbar(errorMessage, 'error');
    },

    copyUSSD() {
      navigator.clipboard.writeText(this.ussdCode);
      this.showSnackbar('USSD code copied!', 'success');
    },

    resetForm() {
      this.form = { name: '', username: '', telephone: '', email: '', affiliate_referral: '', password: '' };
      this.phone_number = "Enter Phone Number";
      this.transactionPhoneNumber = '';
      this.phoneErrorMessage = '';
      this.transactionPhoneErrorMessage = '';
      if (this.$refs.signupForm) this.$refs.signupForm.reset();
    },

    showSnackbar(message, type) {
      const colors = { error: 'red darken-4', success: 'teal darken-2', info: 'blue darken-2' };
      this.$store.commit('setSnackBarMessage', message);
      this.$store.commit('setSnackBarColor', colors[type] || 'teal darken-2');
      this.$store.commit('activateSnackBar', true);
    },
  }
};
</script>

<style scoped>
/* ═══════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════ */
.biz-shell {
  --pc-primary: #00479B;
  --pc-teal: #009688;
  --pc-green: #10B981;
  --pc-orange: #F97316;
  --pc-gold: #D4AF37;
  --pc-purple: #8B5CF6;
  min-height: 100vh;
  background: linear-gradient(180deg, #F0F7FF 0%, #F8FAFC 40%, #FFFFFF 100%);
  position: relative;
  overflow-x: hidden;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

.petty-blue { color: #00479B; }
.cash-green { color: #10B981; }
.sas-gold { color: #D4AF37; font-size: 0.55em; font-weight: 700; vertical-align: super; margin-left: 0.15em; }

/* ═══════════════════════════════════════
   AMBIENT
   ═══════════════════════════════════════ */
.ambient-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.3;
  animation: orbDrift 20s ease-in-out infinite alternate;
}

.orb-blue { width: 500px; height: 500px; background: #00479B; top: -10%; right: -5%; animation-duration: 25s; }
.orb-teal { width: 400px; height: 400px; background: #009688; bottom: 10%; left: -10%; animation-duration: 30s; animation-delay: -5s; }
.orb-gold { width: 300px; height: 300px; background: #D4AF37; top: 40%; left: 40%; opacity: 0.15; animation-duration: 22s; animation-delay: -10s; }

@keyframes orbDrift {
  from { transform: translate(0, 0) scale(1); }
  to { transform: translate(40px, -40px) scale(1.1); }
}

/* ═══════════════════════════════════════
   TOOLBAR
   ═══════════════════════════════════════ */
.glass-toolbar {
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.6);
  position: relative;
  z-index: 2;
}

.toolbar-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.back-link { text-decoration: none; }

.btn-glass {
  background: rgba(0, 71, 155, 0.08) !important;
  color: #00479B !important;
  transition: all 0.3s ease;
}

.btn-glass:hover {
  background: rgba(0, 71, 155, 0.15) !important;
  transform: translateY(-1px);
}

.toolbar-title-text {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0F172A;
  letter-spacing: -0.02em;
}

/* ═══════════════════════════════════════
   MAIN
   ═══════════════════════════════════════ */
.main-container {
  position: relative;
  z-index: 1;
  padding-top: 1.5rem;
  padding-bottom: 3rem;
}

.biz-content {
  max-width: 520px;
  margin: 0 auto;
}

/* Welcome */
.welcome-block {
  text-align: center;
  margin-bottom: 1.5rem;
}

.welcome-eyebrow {
  display: inline-block;
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #00479B;
  background: rgba(0, 71, 155, 0.06);
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  margin-bottom: 0.75rem;
}

.welcome-title {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin-bottom: 1rem;
  color: #0F172A;
}

.welcome-text {
  font-size: 1rem;
  color: #64748B;
  line-height: 1.7;
  max-width: 420px;
  margin: 0 auto;
}

.price-highlight {
  color: #F97316;
  font-weight: 700;
}

.commission-highlight {
  color: #10B981;
  font-weight: 700;
}

/* Features Strip */
.features-strip {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.feature-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
  box-shadow: 0 2px 4px rgba(0,0,0,0.04);
}

/* Form Card */
.glass-form-card {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.25rem !important;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.02) !important;
}

/* Fields */
.field-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.field-icon-wrap { flex-shrink: 0; }

.field-icon-circle {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: white;
  transition: all 0.3s ease;
}

.teal-glow {
  background: linear-gradient(135deg, #009688, #4DB6AC);
  box-shadow: 0 4px 12px rgba(0, 150, 136, 0.25);
  animation: subtlePulse 3s ease-in-out infinite;
}

.blue-glow {
  background: linear-gradient(135deg, #00479B, #0066CC);
  box-shadow: 0 4px 12px rgba(0, 71, 155, 0.25);
}

.orange-glow {
  background: linear-gradient(135deg, #F97316, #FB923C);
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.25);
}

.purple-glow {
  background: linear-gradient(135deg, #8B5CF6, #A78BFA);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.25);
}

.gold-glow {
  background: linear-gradient(135deg, #D4AF37, #F0D878);
  box-shadow: 0 4px 12px rgba(212, 175, 55, 0.25);
}

@keyframes subtlePulse {
  0%, 100% { box-shadow: 0 4px 12px rgba(0, 150, 136, 0.25); }
  50% { box-shadow: 0 6px 20px rgba(0, 150, 136, 0.4); }
}

.field-input-wrap { flex: 1; min-width: 0; }

.modern-input >>> .v-input__slot {
  background: rgba(241, 245, 249, 0.6) !important;
  border-radius: 0.75rem !important;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02) !important;
  border: 1px solid rgba(226, 232, 240, 0.8) !important;
  transition: all 0.3s ease;
  min-height: 48px;
  padding: 0 0.75rem !important;
}

.modern-input.v-input--is-focused >>> .v-input__slot {
  background: white !important;
  border-color: rgba(0, 150, 136, 0.4) !important;
  box-shadow: 0 0 0 3px rgba(0, 150, 136, 0.08), 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
}

.modern-input >>> input {
  font-size: 1rem;
  font-weight: 500;
  color: #0F172A;
}

.modern-input >>> .v-label {
  color: #94A3B8;
  font-weight: 500;
}

/* Phone */
.phone-wrap { position: relative; }

.phone-input {
  width: 100%;
  padding: 0.75rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(226, 232, 240, 0.8);
  background: rgba(241, 245, 249, 0.6);
  font-size: 1rem;
  font-weight: 500;
  color: #0F172A;
  outline: none;
  transition: all 0.3s ease;
  min-height: 48px;
}

.phone-input:focus {
  background: white;
  border-color: rgba(249, 115, 22, 0.4);
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.08), 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.phone-input.has-error {
  border-color: #EF4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.08);
}

.field-error {
  font-size: 12px;
  color: #EF4444;
  margin-top: 4px;
  font-weight: 500;
}

/* intl-tel-input overrides */
.phone-wrap >>> .iti { width: 100%; }
.phone-wrap >>> .iti__flag-container { padding: 0 0.5rem; }
.phone-wrap >>> .iti__selected-flag { border-radius: 0.5rem 0 0 0.5rem; }

/* Help Button */
.help-btn {
  width: 28px !important;
  height: 28px !important;
  border: 1.5px solid #CBD5E1 !important;
  color: #64748B !important;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.help-btn:hover {
  border-color: #00479B !important;
  color: #00479B !important;
  background: rgba(0, 71, 155, 0.04) !important;
}

.help-btn i { font-size: 0.7rem; }

/* Referral Group */
.referral-group {
  background: rgba(255, 251, 235, 0.5);
  border-radius: 0.75rem;
  padding: 0.5rem;
  border: 1px dashed rgba(212, 175, 55, 0.3);
}

/* Submit */
.submit-wrap { margin-top: 1.5rem; }

.btn-glass-primary {
  background: linear-gradient(135deg, #00479B, #0066CC) !important;
  color: white !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 14px rgba(0, 71, 155, 0.3) !important;
  transition: all 0.3s ease;
  height: 48px !important;
}

.btn-glass-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 71, 155, 0.4) !important;
}

.btn-large { font-size: 1rem !important; }

/* Login Link */
.login-link-wrap {
  text-align: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(226, 232, 240, 0.6);
}

.login-text {
  color: #64748B;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.login-link {
  color: #00479B;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.2s;
}

.login-link:hover {
  color: #0066CC;
  text-decoration: underline;
}

/* Trust */
.biz-trust-footer {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 2rem;
  flex-wrap: wrap;
}

.trust-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
  box-shadow: 0 2px 4px rgba(0,0,0,0.04);
}

/* ═══════════════════════════════════════
   PAYMENT DIALOG
   ═══════════════════════════════════════ */
.payment-dialog-glass {
  border-radius: 1.5rem !important;
  overflow: hidden;
}

.payment-glass-card {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.5rem !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15) !important;
  overflow: hidden;
}

.payment-header {
  background: linear-gradient(135deg, #00479B, #0066CC, #009688);
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
}

.payment-header::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle at 50% 0%, rgba(255,255,255,0.2), transparent 60%);
  pointer-events: none;
}

.payment-header-content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 1rem;
  color: white;
}

.payment-title {
  font-size: 1.25rem;
  font-weight: 700;
}

.payment-subtitle {
  font-size: 0.9rem;
  opacity: 0.9;
  font-weight: 600;
}

/* Operators */
.operators-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin: 1.5rem 0;
}

.operator-card-glass {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(8px);
  border: 2px solid transparent;
  border-radius: 1rem !important;
  padding: 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;
}

.operator-card-glass:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 71, 155, 0.1);
}

.operator-card-glass.is-selected {
  border-color: #00479B;
  background: linear-gradient(135deg, #E3F2FD, #BBDEFB);
  box-shadow: 0 8px 20px rgba(0, 71, 155, 0.15);
}

.operator-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #475569;
  margin-top: 0.5rem;
}

.operator-check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00479B, #0066CC);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: popIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes popIn {
  from { transform: scale(0); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

/* Payment Summary */
.payment-summary {
  background: rgba(241, 245, 249, 0.6);
  border-radius: 0.75rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.summary-text {
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.6;
  margin: 0;
}

/* USSD Section */
.ussd-section {
  overflow: hidden;
}

.expand-enter-active, .expand-leave-active {
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  max-height: 300px;
  opacity: 1;
}

.expand-enter, .expand-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.ussd-intro {
  font-size: 0.85rem;
  color: #64748B;
  margin-bottom: 0.75rem;
}

.ussd-code-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #0F172A;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
}

.ussd-code {
  flex: 1;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 1rem;
  color: #4ADE80;
  background: transparent;
  border: none;
  outline: none;
}

.copy-btn {
  color: #94A3B8 !important;
  transition: all 0.2s;
}

.copy-btn:hover {
  color: white !important;
  background: rgba(255,255,255,0.1) !important;
}

.ussd-steps {
  list-style: none;
  padding: 0;
  margin: 0;
}

.ussd-steps li {
  font-size: 0.85rem;
  color: #475569;
  padding: 0.35rem 0;
  padding-left: 1.25rem;
  position: relative;
}

.ussd-steps li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #00479B;
  font-weight: 700;
}

/* Payment Actions */
.payment-actions {
  padding: 1rem 1.5rem;
  background: rgba(241, 245, 249, 0.4);
  border-top: 1px solid rgba(226, 232, 240, 0.5);
}

.btn-cancel {
  font-weight: 600 !important;
  text-transform: none !important;
}

.btn-confirm {
  background: linear-gradient(135deg, #00479B, #0066CC) !important;
  font-weight: 700 !important;
  text-transform: none !important;
  border-radius: 0.75rem !important;
  box-shadow: 0 4px 14px rgba(0, 71, 155, 0.3) !important;
  transition: all 0.3s ease;
}

.btn-confirm:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 71, 155, 0.4) !important;
}

/* ═══════════════════════════════════════
   ANIMATIONS
   ═══════════════════════════════════════ */
.animate-enter {
  opacity: 0;
  transform: translateY(20px);
  animation: enterUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes enterUp {
  to { opacity: 1; transform: translateY(0); }
}

/* ═══════════════════════════════════════
   RESPONSIVE
   ═══════════════════════════════════════ */
@media (max-width: 600px) {
  .glass-toolbar { padding: 0 0.5rem; }
  .toolbar-title-text { font-size: 1.1rem; }
  .main-container { padding-top: 1rem; }
  .welcome-title { font-size: 1.75rem; }
  .glass-form-card { padding: 1.25rem !important; }
  .field-icon-circle { width: 38px; height: 38px; font-size: 1rem; }
  .field-group { gap: 0.5rem; }
  .operators-grid { gap: 0.75rem; }
  .payment-glass-card { border-radius: 1rem !important; }
  .payment-header { padding: 1.25rem; }
}

@media (min-width: 960px) {
  .main-container { padding-top: 2.5rem; }
  .glass-form-card { padding: 2rem !important; }
}
</style>